import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { loginRoute } from '@app/navigation';
import { PipelineAccountScreen } from './PipelineAccountScreen';
import { useCurrentUser } from './useCurrentUser';

export interface RequireAuthProps {
  children: ReactNode;
  /** O que mostrar enquanto `getMe` não responde. */
  fallback?: ReactNode;
}

/**
 * Guard de sessão (FR-007).
 *
 * Sem sessão (401 `nao_autenticado`/`sessao_expirada`, em `getMe` ou em qualquer
 * outra chamada) leva a T1 com `?retorno=` da rota de origem, para voltar a ela
 * depois do login.
 *
 * Falha que **não** é de sessão (rota ainda não implementada no backend, serviço
 * fora, rede) não expulsa ninguém: o console segue em modo degradado e cada tela
 * mostra o seu próprio erro. Quem decide toda ação é o servidor (FR-009).
 *
 * Sessão da conta da esteira (`pipeline`) não entra: vê `PipelineAccountScreen`.
 */
export function RequireAuth({ children, fallback = null }: RequireAuthProps) {
  const { estado, expirada, papel } = useCurrentUser();
  const location = useLocation();

  if (estado === 'carregando') return <>{fallback}</>;

  if (estado === 'anonimo') {
    const origem = `${location.pathname}${location.search}${location.hash}`;
    // `expirada` só quando a sessão morreu em uso — quem chegou pela URL sem
    // sessão nenhuma não deve ler "sua sessão expirou".
    return <Navigate to={loginRoute(origem, expirada)} replace />;
  }

  // A conta da esteira de CI (aim-57a.4) tem sessão e não tem console: o servidor
  // recusa a ela toda leitura além de `getMe`. Ela vê uma tela só, que diz isso e
  // oferece «Sair», no lugar da moldura cheia de recusas.
  if (papel === 'pipeline') return <PipelineAccountScreen />;

  return <>{children}</>;
}
